import { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Search, Plus, Sun, Flame } from "lucide-react";
import { toast } from "sonner";
import { InvoiceKpis } from "@/components/rechnungen/InvoiceKpis";
import { InvoiceCell } from "@/components/rechnungen/InvoiceCell";
import { InvoiceDialog } from "@/components/rechnungen/InvoiceDialog";
import { INVOICE_STATUS, INVOICE_TYPES, STANDARD_TYPES, fmtEUR } from "@/components/rechnungen/shared";

function AuftragssummeInput({ project, onSaved }) {
  const [val, setVal] = useState(project.auftragssumme ?? "");
  useEffect(() => setVal(project.auftragssumme ?? ""), [project.auftragssumme]);
  const save = async () => {
    const n = parseFloat(String(val).replace(/\./g, "").replace(",", "."));
    const next = String(val).trim() === "" || Number.isNaN(n) ? null : n;
    if (next === (project.auftragssumme ?? null)) return;
    try {
      await api.patch(`/rechnungen/projects/${project.id}/auftragssumme`, { auftragssumme: next });
      toast.success("Auftragssumme gespeichert"); onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <Input value={val} onChange={(e) => setVal(e.target.value)} onBlur={save} onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
      inputMode="decimal" placeholder="€" className="h-8 w-28 text-xs tabular-nums" data-testid={`auftragssumme-${project.id}`} />
  );
}

function matches(p, { q, typ, status }) {
  const term = q.trim().toLowerCase();
  if (term) {
    const hay = [p.kunde_name, p.kunde_ort, p.auftraggeber_name, ...(p.rechnungen || []).map((r) => r.rechnungsnummer)].join(" ").toLowerCase();
    if (!hay.includes(term)) return false;
  }
  if (status === "alle") return true;
  return (p.rechnungen || []).some((r) => (typ === "alle" || r.typ === typ) && r.status === status);
}

export default function Rechnungen() {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ q: "", typ: "alle", status: "alle" });
  const [dlg, setDlg] = useState({ open: false, project: null, invoice: null });

  const load = useCallback(async () => {
    try { setProjects((await api.get("/rechnungen/overview")).data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { if (user?.role === "admin") load(); }, [user, load]);

  const visible = useMemo(() => projects.filter((p) => matches(p, filters)), [projects, filters]);
  if (user && user.role !== "admin") return <Navigate to="/" replace />;

  const openInv = (project, invoice) => setDlg({ open: true, project, invoice });
  const setF = (k) => (v) => setFilters((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));

  return (
    <div className="p-4 lg:p-8 space-y-6 animate-fade-up" data-testid="rechnungen-page">
      <div>
        <div className="overline">Buchhaltung</div>
        <h1 className="font-heading text-3xl lg:text-4xl font-bold tracking-tight mt-2">Rechnungen</h1>
        <p className="text-muted-foreground mt-1 text-sm lg:text-base">Anzahlungen, Abschläge und Schlussrechnungen aller Projekte im Blick.</p>
      </div>

      <InvoiceKpis projects={projects} />

      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input className="pl-9" placeholder="Kunde, Ort, Auftraggeber, Rechnungsnr. …" value={filters.q} onChange={setF("q")} data-testid="rechnungen-search" />
        </div>
        <Select value={filters.typ} onValueChange={setF("typ")}>
          <SelectTrigger className="w-48" data-testid="rechnungen-filter-typ"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="alle">Alle Rechnungsarten</SelectItem>
            {Object.entries(INVOICE_TYPES).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.status} onValueChange={setF("status")}>
          <SelectTrigger className="w-44" data-testid="rechnungen-filter-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="alle">Alle Status</SelectItem>
            {Object.entries(INVOICE_STATUS).map(([k, s]) => <SelectItem key={k} value={k}>{s.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <Table data-testid="rechnungen-table">
            <TableHeader>
              <TableRow>
                <TableHead>Kunde</TableHead>
                <TableHead>Auftragssumme</TableHead>
                {STANDARD_TYPES.map((t) => <TableHead key={t}>{INVOICE_TYPES[t]}</TableHead>)}
                <TableHead>Weitere</TableHead>
                <TableHead className="text-right">Bezahlt / Offen</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-10">Lädt …</TableCell></TableRow>}
              {!loading && visible.length === 0 && (
                <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-10" data-testid="rechnungen-empty">Keine Projekte gefunden.</TableCell></TableRow>
              )}
              {visible.map((p) => <ProjectRow key={p.id} p={p} onOpen={openInv} onSaved={load} />)}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <InvoiceDialog open={dlg.open} onOpenChange={(o) => setDlg((d) => ({ ...d, open: o }))}
        project={dlg.project} invoice={dlg.invoice} onSaved={load} />
    </div>
  );
}

function ProjectRow({ p, onOpen, onSaved }) {
  const inv = p.rechnungen || [];
  const std = (t) => inv.find((r) => r.typ === t);
  const extra = inv.filter((r) => !STANDARD_TYPES.includes(r.typ) || inv.filter((x) => x.typ === r.typ)[0] !== r);
  const bezahlt = inv.filter((r) => r.status === "bezahlt").reduce((s, r) => s + (Number(r.betrag) || 0), 0);
  const rest = p.auftragssumme != null ? p.auftragssumme - bezahlt : null;
  const TypeIcon = p.project_type === "waermepumpe" ? Flame : Sun;
  return (
    <TableRow data-testid={`rechnungen-row-${p.id}`}>
      <TableCell className="min-w-[180px]">
        <div className="flex items-center gap-2 font-medium"><TypeIcon className="h-4 w-4 text-primary shrink-0" />{p.kunde_name || "–"}</div>
        <div className="text-xs text-muted-foreground">{[p.kunde_ort, p.auftraggeber_name].filter(Boolean).join(" · ")}</div>
      </TableCell>
      <TableCell><AuftragssummeInput project={p} onSaved={onSaved} /></TableCell>
      {STANDARD_TYPES.map((t) => (
        <TableCell key={t} className="min-w-[140px] align-top">
          {std(t) ? <InvoiceCell invoice={std(t)} onClick={() => onOpen(p, std(t))} testid={`invoice-cell-${p.id}-${t}`} />
            : <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => onOpen(p, { typ: t, titel: INVOICE_TYPES[t] })} data-testid={`invoice-add-${p.id}-${t}`}><Plus className="h-3 w-3 mr-1" />Anlegen</Button>}
        </TableCell>
      ))}
      <TableCell className="min-w-[160px] align-top">
        <div className="space-y-1">
          {extra.map((r) => (
            <div key={r.id}>
              <div className="text-[10px] text-muted-foreground truncate">{r.titel || INVOICE_TYPES[r.typ]}</div>
              <InvoiceCell invoice={r} onClick={() => onOpen(p, r)} testid={`invoice-cell-${p.id}-${r.id}`} />
            </div>
          ))}
          <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => onOpen(p, null)} data-testid={`invoice-add-extra-${p.id}`}>
            <Plus className="h-3 w-3 mr-1" />Rechnung
          </Button>
        </div>
      </TableCell>
      <TableCell className="text-right tabular-nums text-sm whitespace-nowrap">
        <div className="text-emerald-600 dark:text-emerald-400 font-medium">{fmtEUR(bezahlt)}</div>
        {rest != null && <div className="text-xs text-muted-foreground">offen {fmtEUR(rest)}</div>}
      </TableCell>
    </TableRow>
  );
}
